/**
 * Phase 99b (10/07/2026) — Refill éclair (mobile-first).
 *
 * Modale accessible depuis la fiche Distributeur qui permet à l'utilisateur
 * de saisir depuis son téléphone (typiquement en vacances) ce que Pongsak
 * a physiquement chargé, en 30 secondes / distrib.
 *
 * Sortie : crée une tournée AM auto-CLOTUREE avec `reappro` par slot + WASTE
 * pour les DLC retirées → rafraîchit la baseline MOMA-calc.
 *
 * Phase 101fj (Feb 2026) — Purge FIFO auto : chaque slot avec Ajouté > 0
 * doit avoir un lot Conthey sélectionné (source_lot_id) ; chaque slot avec
 * Retirées > 0 doit avoir un lot distrib sélectionné (return_lot_id).
 * Zéro attribution automatique — libre arbitre opérateur total.
 *
 * UX :
 *   - Datetime picker (défaut = maintenant, éditable — refills matin/soir)
 *   - Tableau par slot du distributeur : Cap · Stock AM · Ajouté · Lot source · DLC retirées · Lot retiré
 *   - 2 boutons magiques : « ⚡ Remplir à capacité » et « 🔄 Copier DLC → Ajouté »
 *   - Gros boutons +/- tactiles sur "Ajouté" et "DLC"
 *   - Badge rouge « Lot ! » sur chaque ligne où qty > 0 sans lot choisi
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Loader2, Rocket, Zap, Repeat, Plus, Minus, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function toLocalInputDatetime(iso) {
  const d = iso ? new Date(iso) : new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

function fmtDlc(dlc) {
  if (!dlc) return "—";
  const iso = String(dlc).slice(0, 10);
  const [y, m, d] = iso.split("-");
  return d && m ? `${d}/${m}` : iso;
}

export default function QuickRefillDialog({ open, onOpenChange, distributor, onRefilled }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [slots, setSlots] = useState([]);
  const [refilledAtLocal, setRefilledAtLocal] = useState(toLocalInputDatetime());
  // Phase 101fj — Lots Conthey (source) dispo par product_id (cache)
  const [sourceLotsCache, setSourceLotsCache] = useState({});
  const [distributorLotsCache, setDistributorLotsCache] = useState({});

  useEffect(() => {
    if (!open || !distributor?.id) return;
    setRefilledAtLocal(toLocalInputDatetime());
    setSourceLotsCache({});
    setDistributorLotsCache({});
    (async () => {
      setLoading(true);
      try {
        // Utilise l'endpoint MOMA-reassort pour récupérer les slots + stock AM courant
        const { data } = await api.get(`/distributors/${distributor.id}/moma-reassort`);
        const rows = (data.slots || []).map((s) => ({
          product_id: s.product_id,
          product_name: s.product_name,
          product_sku: s.product_sku,
          slot_number: s.slot_number,
          capacity_max: s.capacity_max || 0,
          qty_am_lots: s.qty_am_lots || 0,
          added: 0,
          dlc_removed: 0,
          // Phase 101fj — Lots explicitement choisis par l'opérateur
          source_lot_id: "",
          source_lot_number: "",
          source_lot_dlc: "",
          return_lot_id: "",
          return_lot_number: "",
          return_lot_dlc: "",
          note: "",
        }));
        setSlots(rows);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur chargement slots");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, distributor?.id]);

  const totals = useMemo(() => {
    let added = 0, dlc = 0, ttl = 0;
    for (const s of slots) {
      added += Number(s.added || 0);
      dlc += Number(s.dlc_removed || 0);
      ttl += 1;
    }
    return {
      added: Math.round(added * 100) / 100,
      dlc: Math.round(dlc * 100) / 100,
      slot_count: ttl,
    };
  }, [slots]);

  const updateRow = (idx, patch) => {
    setSlots((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };
  const bump = (idx, field, delta) => {
    setSlots((prev) => {
      const next = [...prev];
      const cur = Number(next[idx][field] || 0);
      next[idx] = { ...next[idx], [field]: Math.max(0, cur + delta) };
      return next;
    });
  };

  const fillToCapacity = () => {
    setSlots((prev) =>
      prev.map((s) => ({
        ...s,
        added: Math.max(0, Number(s.capacity_max || 0) - Number(s.qty_am_lots || 0)),
      }))
    );
    toast.success("Slots pré-remplis à capacité");
  };

  const copyDlcToAdded = () => {
    setSlots((prev) =>
      prev.map((s) =>
        Number(s.dlc_removed || 0) > 0
          ? { ...s, added: Number(s.dlc_removed || 0) }
          : s
      )
    );
    toast.success("Retirées copiées vers Ajouté (1-pour-1)");
  };

  // Phase 101fj — Lots manquants (badges rouges + blocage submit)
  const missingLots = useMemo(() => {
    const errs = [];
    for (const s of slots) {
      if (Number(s.added || 0) > 0 && !s.source_lot_id) {
        errs.push({ slot: s.slot_number, product: s.product_name, kind: "source" });
      }
      if (Number(s.dlc_removed || 0) > 0 && !s.return_lot_id) {
        errs.push({ slot: s.slot_number, product: s.product_name, kind: "return" });
      }
    }
    return errs;
  }, [slots]);

  const loadSourceLots = async (product_id) => {
    if (sourceLotsCache[product_id]) return sourceLotsCache[product_id];
    try {
      const { data } = await api.get(`/moma-refills/available-lots/${product_id}`);
      const lots = (data.lots || []).sort((a, b) =>
        (a.lot_number || "").localeCompare(b.lot_number || "", "fr", { numeric: true })
      );
      setSourceLotsCache((prev) => ({ ...prev, [product_id]: lots }));
      return lots;
    } catch (err) {
      toast.error(`Erreur lots Conthey : ${err.response?.data?.detail || err.message}`);
      return [];
    }
  };

  const loadDistributorLots = async (product_id) => {
    const cacheKey = `${distributor.id}::${product_id}`;
    if (distributorLotsCache[cacheKey]) return distributorLotsCache[cacheKey];
    try {
      const { data } = await api.get(`/lots`, {
        params: { distributor_id: distributor.id, product_id },
      });
      const lots = (Array.isArray(data) ? data : data?.lots || [])
        .filter((l) => (l.quantity || 0) > 0)
        .sort((a, b) => {
          const da = a.dlc || "9999-12-31";
          const db = b.dlc || "9999-12-31";
          return da.localeCompare(db);
        });
      setDistributorLotsCache((prev) => ({ ...prev, [cacheKey]: lots }));
      return lots;
    } catch (err) {
      toast.error(`Erreur lots distrib : ${err.response?.data?.detail || err.message}`);
      return [];
    }
  };

  const submit = async () => {
    // Phase 101fj — Blocage si des lots manquent
    if (missingLots.length > 0) {
      const pretty = missingLots.slice(0, 3).map((m) => `slot ${m.slot} (${m.kind === "source" ? "lot Conthey" : "lot retiré"})`).join(", ");
      toast.error(
        `Sélection manuelle des lots requise : ${missingLots.length} ligne(s) — ex: ${pretty}. Aucune attribution FIFO auto.`,
        { duration: 6000 }
      );
      return;
    }
    const linesToSend = slots
      .filter((s) => Number(s.added || 0) > 0 || Number(s.dlc_removed || 0) > 0)
      .map((s) => ({
        product_id: s.product_id,
        slot_number: s.slot_number,
        added: Number(s.added || 0),
        dlc_removed: Number(s.dlc_removed || 0),
        source_lot_id: s.source_lot_id || null,
        return_lot_id: s.return_lot_id || null,
        note: (s.note || "").trim(),
      }));

    if (linesToSend.length === 0) {
      toast.warning("Aucun changement à enregistrer (Ajouté = 0 et DLC = 0 partout)");
      return;
    }

    setSubmitting(true);
    try {
      // Convertit datetime local → ISO UTC
      const refilledIso = new Date(refilledAtLocal).toISOString();
      const { data } = await api.post(`/refill-tours/quick-refill`, {
        distributor_id: distributor.id,
        refilled_at: refilledIso,
        lines: linesToSend,
        operator_name: "Refill éclair mobile",
      });
      const nbReturns = (data.dlc_return_movements || []).length;
      toast.success(
        `Refill éclair enregistré : ${data.reference} · ${data.lines_count} slot(s)` +
          (nbReturns > 0 ? ` · ${nbReturns} retour(s) Conthey` : "")
      );
      if (data.warnings?.length) data.warnings.forEach((w) => toast.warning(w));
      onRefilled && onRefilled(data);
      onOpenChange(false);
    } catch (e) {
      // Phase 101fi — Erreur structurée pour stock insuffisant (Alain)
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "insufficient_central_stock") {
        const lines = (detail.missing || []).map(
          (m) => `• ${m.product_name} : demandé ${m.needed}, dispo Conthey ${m.available}`
        ).join("\n");
        toast.error(
          `Stock Conthey insuffisant — fabrique ou transfère d'abord ces barquettes :\n${lines}`,
          { duration: 8000 }
        );
      } else if (typeof detail === "object" && detail?.error === "manual_lot_selection_required") {
        // Phase 101fj — Backend rejette si un lot manque
        toast.error(detail.message || "Sélection manuelle des lots requise (aucun FIFO auto)", {
          duration: 8000,
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur refill éclair");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] flex flex-col overflow-hidden"
        data-testid="quick-refill-dialog"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-[#1F4E3D]" />
            Refill éclair — {distributor?.name || "Distributeur"}
          </DialogTitle>
          <DialogDescription>
            Enregistre ce que Pongsak a physiquement chargé aujourd&apos;hui. Crée
            une tournée auto-clôturée + retour Conthey des barquettes retirées
            (rejet définitif fait ensuite depuis Conthey/Stocks). Baseline MOMA-calc
            mise à jour.
            <span className="block mt-1 text-[11px] text-[#8C6B2F]">
              Sélection manuelle du lot obligatoire (aucun FIFO auto — libre arbitre opérateur).
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* Phase 101fj — Bandeau d'alerte si des lots manquent */}
        {missingLots.length > 0 && (
          <div
            className="flex-shrink-0 flex items-start gap-2 rounded-md bg-[#FBE9E4] border border-[#C05C47]/40 text-[#7A1F1F] px-3 py-2 text-xs"
            data-testid="quick-refill-missing-lots-banner"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <b>{missingLots.length} ligne(s)</b> sans lot choisi — la validation est bloquée
              tant que chaque ajout / retrait n'a pas son lot explicite.
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="quick-refill-scroll-body">
        {/* Date/heure */}
        <div className="border border-[#E5E5E0] p-3 bg-[#FAFAF7]">
          <label className="text-xs uppercase tracking-wider text-[#6B6E6B] mb-1 block">
            Date & heure du passage
          </label>
          <Input
            type="datetime-local"
            value={refilledAtLocal}
            onChange={(e) => setRefilledAtLocal(e.target.value)}
            className="font-mono"
            data-testid="quick-refill-datetime"
          />
        </div>

        {loading ? (
          <div className="p-8 text-center text-[#575A57]">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement des slots…
          </div>
        ) : slots.length === 0 ? (
          <div className="p-6 text-center text-[#575A57]">
            Aucun slot configuré pour ce distributeur.
          </div>
        ) : (
          <>
            {/* Boutons magiques */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={fillToCapacity}
                className="border-[#1F4E3D]/40 text-[#1F4E3D] hover:bg-[#EAF4EE]"
                data-testid="quick-refill-fill-capacity"
                title="Pré-remplit Ajouté = capacité − stock AM courant"
              >
                <Zap className="w-4 h-4 mr-1" /> Remplir à capacité
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={copyDlcToAdded}
                className="border-[#8C6B2F]/40 text-[#8C6B2F] hover:bg-[#FBF4E9]"
                data-testid="quick-refill-copy-dlc"
                title="Pour chaque slot avec Retirées>0, met Ajouté = Retirées (remplacement 1-pour-1)"
              >
                <Repeat className="w-4 h-4 mr-1" /> Copier Retirées → Ajouté
              </Button>
              <div className="flex-1" />
              <div className="text-xs text-[#575A57] self-center">
                {totals.slot_count} slot(s) · +{totals.added} chargé(s) · {totals.dlc} retour(s) Conthey
              </div>
            </div>

            {/* Tableau slots */}
            <div className="border border-[#E5E5E0] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="quick-refill-slots-table">
                  <thead className="bg-[#FAFAF7]">
                    <tr>
                      <th className="text-left px-2 py-2 w-12">Slot</th>
                      <th className="text-left px-2 py-2">Produit</th>
                      <th className="text-center px-2 py-2 w-12">Cap</th>
                      <th className="text-center px-2 py-2 w-12">AM</th>
                      <th className="text-center px-2 py-2 w-32">Ajouté</th>
                      <th className="text-left px-2 py-2 w-40">Lot Conthey</th>
                      <th className="text-center px-2 py-2 w-32">Retirées → Conthey</th>
                      <th className="text-left px-2 py-2 w-40">Lot retiré</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map((s, idx) => {
                      const needSource = Number(s.added || 0) > 0 && !s.source_lot_id;
                      const needReturn = Number(s.dlc_removed || 0) > 0 && !s.return_lot_id;
                      return (
                      <tr
                        key={`${s.product_id}-${s.slot_number}`}
                        className="border-t border-[#E5E5E0]"
                        data-testid={`quick-refill-row-${s.slot_number}`}
                      >
                        <td className="px-2 py-2 font-mono font-semibold">
                          {s.slot_number}
                        </td>
                        <td className="px-2 py-2">
                          <div className="font-medium">{s.product_name}</div>
                          <div className="text-[9px] text-[#6B6E6B] font-mono">
                            {s.product_sku}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center font-mono text-[#575A57]">
                          {s.capacity_max}
                        </td>
                        <td className="px-2 py-2 text-center font-mono text-[#575A57]">
                          {s.qty_am_lots}
                        </td>
                        <td className="px-1 py-1">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              className="h-9 w-9 border-[#C05C47]/40 text-[#C05C47]"
                              onClick={() => bump(idx, "added", -1)}
                              data-testid={`quick-refill-added-minus-${s.slot_number}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <Input
                              type="number"
                              step="1"
                              min="0"
                              value={s.added}
                              onChange={(e) =>
                                updateRow(idx, {
                                  added:
                                    e.target.value === "" ? 0 : Number(e.target.value),
                                })
                              }
                              className="text-center h-9 font-mono w-12"
                              data-testid={`quick-refill-added-${s.slot_number}`}
                            />
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              className="h-9 w-9 border-[#1F4E3D]/40 text-[#1F4E3D]"
                              onClick={() => bump(idx, "added", 1)}
                              data-testid={`quick-refill-added-plus-${s.slot_number}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                        {/* Phase 101fj — Lot Conthey (source) */}
                        <td className="px-1 py-1">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => loadSourceLots(s.product_id)}
                                className={`w-full h-9 justify-start font-mono text-[11px] ${needSource ? "border-[#C05C47] bg-[#FBE9E4]" : ""}`}
                                data-testid={`quick-refill-source-lot-btn-${s.slot_number}`}
                              >
                                {s.source_lot_number ? (
                                  <span className="truncate">{s.source_lot_number} · {fmtDlc(s.source_lot_dlc)}</span>
                                ) : (
                                  <span className={needSource ? "text-[#C05C47] font-bold flex items-center gap-1" : "text-[#A3A6A3]"}>
                                    {needSource && <AlertTriangle className="w-3 h-3" />}
                                    {needSource ? "Lot requis" : "—"}
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 p-0" align="start">
                              <div className="max-h-72 overflow-y-auto">
                                {(sourceLotsCache[s.product_id] || []).length === 0 ? (
                                  <div className="p-3 text-center text-xs text-[#A3A6A3] italic">
                                    Aucun lot dispo à Conthey.
                                  </div>
                                ) : (
                                  (sourceLotsCache[s.product_id] || []).map((l) => (
                                    <button
                                      key={l.id}
                                      type="button"
                                      onClick={() =>
                                        updateRow(idx, {
                                          source_lot_id: l.id,
                                          source_lot_number: l.lot_number,
                                          source_lot_dlc: l.dlc || "",
                                        })
                                      }
                                      className={`w-full flex justify-between px-3 py-2 text-xs hover:bg-[#F5F5F0] border-b border-[#F0F0EC] last:border-0 ${
                                        s.source_lot_id === l.id ? "bg-[#EAF4EE]" : ""
                                      }`}
                                      data-testid={`quick-refill-source-lot-opt-${s.slot_number}-${l.id}`}
                                    >
                                      <span className="font-mono">{l.lot_number}</span>
                                      <span className="text-[#575A57]">DLC {fmtDlc(l.dlc)} · qty {l.quantity}</span>
                                    </button>
                                  ))
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </td>
                        <td className="px-1 py-1">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              className="h-9 w-9"
                              onClick={() => bump(idx, "dlc_removed", -1)}
                              data-testid={`quick-refill-dlc-minus-${s.slot_number}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <Input
                              type="number"
                              step="1"
                              min="0"
                              value={s.dlc_removed}
                              onChange={(e) =>
                                updateRow(idx, {
                                  dlc_removed:
                                    e.target.value === "" ? 0 : Number(e.target.value),
                                })
                              }
                              className="text-center h-9 font-mono w-12"
                              data-testid={`quick-refill-dlc-${s.slot_number}`}
                            />
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              className="h-9 w-9 border-[#8C6B2F]/40 text-[#8C6B2F]"
                              onClick={() => bump(idx, "dlc_removed", 1)}
                              data-testid={`quick-refill-dlc-plus-${s.slot_number}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                        {/* Phase 101fj — Lot distributeur (à retirer) */}
                        <td className="px-1 py-1">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => loadDistributorLots(s.product_id)}
                                className={`w-full h-9 justify-start font-mono text-[11px] ${needReturn ? "border-[#C05C47] bg-[#FBE9E4]" : ""}`}
                                data-testid={`quick-refill-return-lot-btn-${s.slot_number}`}
                              >
                                {s.return_lot_number ? (
                                  <span className="truncate">{s.return_lot_number} · {fmtDlc(s.return_lot_dlc)}</span>
                                ) : (
                                  <span className={needReturn ? "text-[#C05C47] font-bold flex items-center gap-1" : "text-[#A3A6A3]"}>
                                    {needReturn && <AlertTriangle className="w-3 h-3" />}
                                    {needReturn ? "Lot requis" : "—"}
                                  </span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 p-0" align="start">
                              <div className="max-h-72 overflow-y-auto">
                                {(distributorLotsCache[`${distributor.id}::${s.product_id}`] || []).length === 0 ? (
                                  <div className="p-3 text-center text-xs text-[#A3A6A3] italic">
                                    Aucun lot dispo sur ce distributeur.
                                  </div>
                                ) : (
                                  (distributorLotsCache[`${distributor.id}::${s.product_id}`] || []).map((l) => (
                                    <button
                                      key={l.id}
                                      type="button"
                                      onClick={() =>
                                        updateRow(idx, {
                                          return_lot_id: l.id,
                                          return_lot_number: l.lot_number,
                                          return_lot_dlc: l.dlc || "",
                                        })
                                      }
                                      className={`w-full flex justify-between px-3 py-2 text-xs hover:bg-[#F5F5F0] border-b border-[#F0F0EC] last:border-0 ${
                                        s.return_lot_id === l.id ? "bg-[#EAF4EE]" : ""
                                      }`}
                                      data-testid={`quick-refill-return-lot-opt-${s.slot_number}-${l.id}`}
                                    >
                                      <span className="font-mono">{l.lot_number}</span>
                                      <span className="text-[#575A57]">DLC {fmtDlc(l.dlc)} · qty {l.quantity}</span>
                                    </button>
                                  ))
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        </div>

        <DialogFooter className="gap-2 flex-shrink-0 border-t border-[#E5E5E0] pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="quick-refill-cancel">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || loading || slots.length === 0 || missingLots.length > 0}
            className="bg-[#1F4E3D] hover:bg-[#173D2F]"
            data-testid="quick-refill-submit"
            title={missingLots.length > 0 ? `${missingLots.length} lot(s) manquant(s) — sélection manuelle obligatoire` : "Enregistrer & clôturer"}
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            {missingLots.length > 0 ? `Sélectionne ${missingLots.length} lot(s)` : "Enregistrer & clôturer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
